import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Collapse,
    Paper,
    Typography,
} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import useAuth from '../../context/AuthProvider';
import { apiClient } from '../../helper/api';
import { MessageTemplateListItem } from '../../models/messageTemplateTypes';
import { EditMessageTemplateDialog, formatUpdated } from './EditMessageTemplateDialog';
import { MessageTemplateBodyPreview } from './MessageTemplateBodyPreview';
import { templateBadgeSx, templateCardSx } from './messageTemplateListStyles';
import Swal from 'sweetalert2';

export const MessageTemplatesList: React.FC = () => {
    const { user } = useAuth();
    const token = user?.token;

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [templates, setTemplates] = useState<MessageTemplateListItem[]>([]);
    const [expandedKey, setExpandedKey] = useState<string | null>(null);
    const [editOpen, setEditOpen] = useState(false);
    const [editSource, setEditSource] = useState<string | null>(null);
    const [editId, setEditId] = useState<number | null>(null);

    const fetchTemplates = useCallback(() => {
        if (!token) return;
        setLoading(true);
        setError(null);
        apiClient('/MessageTemplates/list', {
            method: 'GET',
            headers: new Headers({
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
            }),
        })
            .then((res) => {
                if (res?.status && Array.isArray(res.data?.templates)) {
                    setTemplates(res.data.templates);
                } else {
                    setError(res?.message || 'Failed to load templates.');
                }
            })
            .catch((err) => setError(err?.message || 'Failed to load templates.'))
            .finally(() => setLoading(false));
    }, [token]);

    useEffect(() => {
        fetchTemplates();
    }, [fetchTemplates]);

    const openEdit = (t: MessageTemplateListItem) => {
        setEditSource(t.source);
        setEditId(t.id);
        setEditOpen(true);
    };

    const toggleActive = async (t: MessageTemplateListItem) => {
        if (!token) return;
        const nextActive = !t.active;
        try {
            const detailRes = await apiClient(
                `/MessageTemplates/detail?source=${encodeURIComponent(t.source)}&id=${t.id}`,
                {
                    method: 'GET',
                    headers: new Headers({
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    }),
                },
            );
            if (!detailRes?.status || !detailRes.data?.template) {
                Swal.fire({ icon: 'error', title: 'Update failed', text: detailRes?.message });
                return;
            }
            const full = detailRes.data.template;
            const res = await apiClient('/MessageTemplates/save', {
                method: 'POST',
                headers: new Headers({
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                }),
                body: JSON.stringify({
                    source: full.source,
                    id: full.id,
                    templateName: full.name,
                    subject: full.subject || full.name,
                    body: full.body,
                    originalBody: full.body,
                    active: nextActive,
                    adminConfirmed: !full.restricted,
                }),
            });
            if (res?.status) {
                fetchTemplates();
            } else {
                Swal.fire({ icon: 'error', title: 'Update failed', text: res?.message });
            }
        } catch (err: unknown) {
            Swal.fire({
                icon: 'error',
                title: 'Update failed',
                text: err instanceof Error ? err.message : 'Unable to update.',
            });
        }
    };

    const cardKey = (t: MessageTemplateListItem) => `${t.source}-${t.id}`;

    return (
        <Box sx={{ height: 'calc(100vh - 170px)', display: 'flex', flexDirection: 'column', width: '100%' }}>
            <Box
                sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 0,
                    borderRadius: '12px',
                    overflow: 'hidden',
                    boxShadow: '0 4px 24px rgba(15, 23, 42, 0.08), 0 1px 3px rgba(15, 23, 42, 0.06)',
                    border: '1px solid #e2e8f0',
                    backgroundColor: '#ffffff',
                }}
            >
                <Box
                    sx={{
                        px: 2,
                        py: 1.5,
                        borderBottom: '1px solid #e2e8f0',
                        backgroundColor: '#fafbfc',
                        flexShrink: 0,
                    }}
                >
                    <Typography
                        component="h1"
                        sx={{ fontWeight: 600, color: '#0f172a', fontSize: '1.25rem', lineHeight: 1.3, mb: 0.5 }}
                    >
                        Message Templates
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Edit outbound client notification content — send logic unchanged
                    </Typography>
                </Box>

                <Box sx={{ flex: 1, overflow: 'auto', px: '22px', py: '22px', minHeight: 0 }}>
                    <Typography
                        variant="body2"
                        sx={{ fontSize: 13, color: '#64748b', mb: 1.75 }}
                    >
                        {templates.length} templates · expand to preview · edit to update content
                    </Typography>
                    {loading && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress />
                        </Box>
                    )}
                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                    {!loading && templates.map((t) => {
                const key = cardKey(t);
                const expanded = expandedKey === key;
                return (
                    <Paper key={key} elevation={0} sx={templateCardSx.root}>
                        <Box
                            sx={{
                                ...templateCardSx.header,
                                ...(expanded ? templateCardSx.headerExpanded : {}),
                            }}
                            onClick={() => setExpandedKey(expanded ? null : key)}
                        >
                            <MailOutlineIcon sx={{ color: '#64748b', fontSize: 16 }} />
                            <Typography sx={templateCardSx.name}>
                                {t.name?.trim() || t.subject?.trim() || `Template #${t.id}`}
                            </Typography>
                            {t.restricted && (
                                <Chip size="small" label="Restricted" sx={templateBadgeSx.restricted} />
                            )}
                            {t.errorCode && (
                                <Chip size="small" label="Error code" sx={templateBadgeSx.errorCode} />
                            )}
                            <Typography sx={templateCardSx.meta}>
                                {formatUpdated(t.dateModified, t.modifiedBy, t.modifiedByName)}
                            </Typography>
                            <Chip
                                size="small"
                                label={t.active ? 'Active' : 'Inactive'}
                                sx={t.active ? templateBadgeSx.active : templateBadgeSx.inactive}
                            />
                            <ExpandMoreIcon
                                sx={{
                                    fontSize: 14,
                                    ml: 0.875,
                                    color: '#64748b',
                                    transform: expanded ? 'rotate(180deg)' : 'none',
                                    transition: 'transform 0.15s',
                                }}
                            />
                        </Box>
                        <Collapse in={expanded}>
                            <Box sx={templateCardSx.body}>
                                {t.note && (
                                    <Alert severity="warning" sx={{ mb: 1.5, py: 0 }}>
                                        {t.note}
                                    </Alert>
                                )}
                                {(t.source === 'email' || t.subject) && (
                                    <Typography sx={templateCardSx.subject}>
                                        Subject: <strong>{t.subject || t.name}</strong>
                                    </Typography>
                                )}
                                <MessageTemplateBodyPreview
                                    content={t.bodyPreview || ''}
                                    sx={templateCardSx.preview}
                                />
                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={<EditOutlinedIcon fontSize="small" />}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            openEdit(t);
                                        }}
                                        sx={{
                                            textTransform: 'none',
                                            borderColor: '#e2e8f0',
                                            color: '#334155',
                                        }}
                                    >
                                        Edit template
                                    </Button>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={
                                            t.active
                                                ? <VisibilityOffOutlinedIcon fontSize="small" />
                                                : <VisibilityOutlinedIcon fontSize="small" />
                                        }
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            toggleActive(t);
                                        }}
                                        sx={{
                                            textTransform: 'none',
                                            borderColor: '#e2e8f0',
                                            color: '#334155',
                                        }}
                                    >
                                        {t.active ? 'Deactivate' : 'Activate'}
                                    </Button>
                                </Box>
                            </Box>
                        </Collapse>
                    </Paper>
                );
                    })}
                </Box>
            </Box>

            <EditMessageTemplateDialog
                open={editOpen}
                source={editSource}
                id={editId}
                onClose={() => {
                    setEditOpen(false);
                    setEditSource(null);
                    setEditId(null);
                }}
                onSaved={fetchTemplates}
            />
        </Box>
    );
};
